/**
 * ProofPage — compounding-evidence surfaces:
 * 1. Daily Study Commitment (target + 30-day grid + met-streak)
 * 2. Mastery Audit Trail (per-subject growth over the last 4 weeks)
 *
 * Distinct from the older ProgressPage (gamification XP/badges) — this
 * is the "I can prove I've grown" page that horizontal LLMs structurally
 * can't show.
 */
import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, Target, Sparkles, Brain, Check, AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api, { apiUrl } from '../utils/apiClient';
import PageSkeleton from '../components/PageSkeleton';

export default function ProofPage() {
  const [commitment, setCommitment] = useState(null);
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingTarget, setSavingTarget] = useState(false);
  const [targetInput, setTargetInput] = useState('');

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const [c, a] = await Promise.all([
          api.get(apiUrl('/progress/commitment')),
          api.get(apiUrl('/progress/mastery-audit')),
        ]);
        if (cancel) return;
        setCommitment(c.data);
        setAudit(a.data);
        setTargetInput(String(c.data.target_minutes_per_day));
      } catch (err) {
        if (!cancel) toast.error('Could not load progress data');
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, []);

  const saveTarget = async () => {
    const n = parseInt(targetInput, 10);
    if (!Number.isFinite(n) || n < 1 || n > 300) {
      toast.error('Target must be between 1 and 300 minutes');
      return;
    }
    setSavingTarget(true);
    try {
      await api.put(apiUrl('/progress/commitment'), { target_minutes_per_day: n });
      toast.success(`Daily target updated to ${n} minutes`);
      const r = await api.get(apiUrl('/progress/commitment'));
      setCommitment(r.data);
    } catch {
      toast.error('Failed to save');
    } finally {
      setSavingTarget(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-12" data-testid="proof-loading">
        <PageSkeleton variant="list" rows={3} />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-12 space-y-8" data-testid="proof-page">
      <div>
        <p className="text-[10px] uppercase tracking-[0.22em] text-[#5C8AFF] font-semibold mb-2">
          Compounding evidence
        </p>
        <h1 className="text-3xl sm:text-4xl font-bold text-[var(--isp-heading)] tracking-tight">
          Your progress, on the record.
        </h1>
        <p className="text-sm text-[var(--isp-muted)] mt-2 max-w-2xl">
          Every day you show up gets logged. Every flashcard that graduates to long-interval mastery accumulates here. Nothing here resets — it just grows.
        </p>
      </div>

      {/* DAILY STUDY COMMITMENT */}
      <section className="border border-[var(--isp-glass-border)] bg-[var(--isp-glass)] p-5 sm:p-6" data-testid="commitment-section">
        <div className="flex items-start justify-between gap-4 mb-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center bg-[#5C8AFF]/10 shrink-0">
              <Target className="w-5 h-5 text-[#5C8AFF]" />
            </div>
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[var(--isp-muted)]">Daily Study Commitment</p>
              <h2 className="text-lg font-bold text-[var(--isp-heading)] tracking-tight">
                {commitment.today_minutes} <span className="text-[var(--isp-muted)] text-sm font-medium">of</span> {commitment.target_minutes_per_day} <span className="text-[var(--isp-muted)] text-sm font-medium">min today</span>
              </h2>
            </div>
          </div>
          <div className="text-right shrink-0">
            <p className="text-[10px] uppercase tracking-[0.18em] text-[#34C759] font-semibold">Met streak</p>
            <p className="text-xl font-bold text-[var(--isp-heading)] tracking-tight" data-testid="commitment-met-streak">
              {commitment.met_streak}<span className="text-sm text-[var(--isp-muted)] font-medium"> day{commitment.met_streak === 1 ? '' : 's'}</span>
            </p>
          </div>
        </div>

        <div className="mb-5">
          <div className="h-2 bg-[var(--isp-glass-inner)] overflow-hidden">
            <motion.div
              className={`h-full ${commitment.today_pct >= 100 ? 'bg-[#34C759]' : 'bg-[#5C8AFF]'}`}
              initial={{ width: 0 }}
              animate={{ width: `${commitment.today_pct}%` }}
              transition={{ duration: 0.6 }}
              data-testid="commitment-today-bar"
            />
          </div>
          <div className="flex items-center justify-between mt-1.5">
            <span className="text-[10px] text-[var(--isp-muted)] tracking-wider uppercase">Today</span>
            <span className="text-[10px] text-[var(--isp-muted)] font-mono">{commitment.today_pct}%</span>
          </div>
        </div>

        <div>
          <p className="text-[10px] uppercase tracking-[0.14em] text-[var(--isp-muted)] mb-2">Last 30 days</p>
          <div className="grid gap-1" data-testid="commitment-grid" style={{ gridTemplateColumns: 'repeat(30, minmax(0, 1fr))' }}>
            {commitment.grid.map((d) => (
              <div
                key={d.date}
                title={`${d.date}: ${d.minutes} min · target ${commitment.target_minutes_per_day}`}
                className={`aspect-square ${d.met ? 'bg-[#34C759]/85' : d.minutes > 0 ? 'bg-[#5C8AFF]/40' : 'bg-[var(--isp-glass-inner)]'} ${d.is_today ? 'ring-1 ring-[#5C8AFF]' : ''}`}
                data-testid={`commitment-cell-${d.date}`}
              />
            ))}
          </div>
          <div className="flex items-center gap-3 mt-3 text-[10px] text-[var(--isp-muted)]">
            <span className="inline-flex items-center gap-1"><span className="w-2 h-2 bg-[var(--isp-glass-inner)]" /> Skipped</span>
            <span className="inline-flex items-center gap-1"><span className="w-2 h-2 bg-[#5C8AFF]/40" /> Partial</span>
            <span className="inline-flex items-center gap-1"><span className="w-2 h-2 bg-[#34C759]/85" /> Hit target</span>
          </div>
        </div>

        <div className="mt-5 pt-4 border-t border-[var(--isp-glass-border)] flex items-center gap-3">
          <label className="text-xs text-[var(--isp-muted)]">Adjust target:</label>
          <input
            type="number"
            min={1}
            max={300}
            value={targetInput}
            onChange={(e) => setTargetInput(e.target.value)}
            className="w-20 px-2 py-1.5 bg-[var(--isp-glass-inner)] border border-[var(--isp-glass-border)] text-sm text-[var(--isp-heading)] focus:outline-none focus:border-[#5C8AFF]"
            data-testid="commitment-target-input"
          />
          <span className="text-xs text-[var(--isp-muted)]">min/day</span>
          <button
            onClick={saveTarget}
            disabled={savingTarget}
            className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#002FA7] hover:bg-[#1A47C2] disabled:bg-white/10 text-white text-xs font-semibold tracking-tight"
            data-testid="commitment-target-save"
          >
            {savingTarget ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
            Save
          </button>
        </div>
      </section>

      {/* MASTERY AUDIT TRAIL */}
      <section className="border border-[var(--isp-glass-border)] bg-[var(--isp-glass)] p-5 sm:p-6" data-testid="mastery-section">
        <div className="flex items-start justify-between gap-4 mb-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center bg-[#34C759]/10 shrink-0">
              <Brain className="w-5 h-5 text-[#34C759]" />
            </div>
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[var(--isp-muted)]">Mastery Audit Trail</p>
              <h2 className="text-lg font-bold text-[var(--isp-heading)] tracking-tight">
                <span data-testid="mastery-overall">{audit.overall_mastered}</span>
                <span className="text-[var(--isp-muted)] text-sm font-medium"> of {audit.overall_total_cards} cards mastered</span>
              </h2>
              <p className="text-xs text-[var(--isp-muted)] mt-0.5">
                Mastery threshold: ≥ {audit.mastery_threshold_days}-day spaced-repetition interval.
              </p>
            </div>
          </div>
          {audit.growth_4w !== 0 && (
            <div className="text-right shrink-0" data-testid="mastery-growth">
              <p className={`text-[10px] uppercase tracking-[0.18em] font-semibold ${audit.growth_4w > 0 ? 'text-[#34C759]' : 'text-[#FF453A]'}`}>4-week growth</p>
              <p className="text-xl font-bold text-[var(--isp-heading)] tracking-tight inline-flex items-center gap-1">
                {audit.growth_4w > 0 ? <TrendingUp className="w-4 h-4 text-[#34C759]" /> : null}
                {audit.growth_4w > 0 ? '+' : ''}{audit.growth_4w}
              </p>
            </div>
          )}
        </div>

        {audit.subjects.length === 0 ? (
          <div className="text-center py-10 text-sm text-[var(--isp-muted)]" data-testid="mastery-empty">
            <Sparkles className="w-8 h-8 text-[var(--isp-muted)] mx-auto mb-2 opacity-40" />
            No flashcards yet. Record a lecture or upload a doc → AI generates your first deck → it'll show up here as soon as cards graduate to long-interval review.
          </div>
        ) : (
          <div className="space-y-2.5">
            {audit.subjects.map((s) => {
              const pct = s.total_cards > 0 ? Math.round((s.mastered_today / s.total_cards) * 100) : 0;
              const maxTrend = Math.max(...s.trend, 1);
              return (
                <div
                  key={s.subject_id || 'unsorted'}
                  className="flex items-center gap-4 py-2 px-3 hover:bg-[var(--isp-glass-inner)] transition-colors"
                  data-testid={`mastery-row-${s.subject_id || 'unsorted'}`}
                >
                  <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: s.color }} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[var(--isp-heading)] truncate">{s.subject_name}</p>
                    <p className="text-xs text-[var(--isp-muted)]">
                      <span className="font-mono">{s.mastered_today}</span> mastered · <span className="font-mono">{s.total_cards}</span> total · <span className="font-mono">{pct}%</span>
                    </p>
                  </div>
                  <div className="flex items-end gap-0.5 h-6 shrink-0" title={`Last 4 weeks: ${s.trend.join(' → ')}`}>
                    {s.trend.map((n, i) => (
                      <div
                        key={`spark-${i}`}
                        className={`w-1.5 ${i === s.trend.length - 1 ? 'bg-[#34C759]' : 'bg-[#5C8AFF]/45'}`}
                        style={{ height: `${(n / maxTrend) * 100}%`, minHeight: '2px' }}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-5 pt-4 border-t border-[var(--isp-glass-border)] flex items-start gap-2">
          <AlertCircle className="w-3.5 h-3.5 text-[var(--isp-muted)] shrink-0 mt-0.5" />
          <p className="text-[11px] text-[var(--isp-muted)] leading-relaxed">
            Mastery here means a card has graduated to a ≥ {audit.mastery_threshold_days}-day review interval — i.e. you reliably remembered it weeks apart. This is a stricter, more honest definition than "I've seen this once."
          </p>
        </div>
      </section>
    </div>
  );
}
